import { Button } from './Button'
import type { ButtonProps } from './Button'
import { Slot, Slottable } from './Slot'
import { tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'
import type {
  ComponentProps,
  HTMLAttributes,
  ReactElement,
  ReactNode,
} from 'react'
import { cloneElement, isValidElement } from 'react'

export const tagVariants = tv({
  base: 'inline-flex items-center gap-x-1.5 rounded-xl border-0 text-start ring-1 transition-colors ring-inset [[type=button]]:not-aria-disabled:cursor-pointer aria-disabled:cursor-not-allowed aria-disabled:opacity-50',

  compoundVariants: [
    {
      className: 'max-w-48 text-pretty',
      overflowBehavior: 'wrap',
      size: 'sm',
    },
  ],

  defaultVariants: {
    overflowBehavior: 'none',
    size: 'md',
    variant: 'neutral',
    weight: 'medium',
  },

  slots: {
    cluster: 'inline-flex min-w-0 flex-1 items-center gap-x-1.5',
    content: 'flex-[1_0_0]',
    dot: 'size-2 shrink-0 rounded-full',
  },

  variants: {
    overflowBehavior: {
      none: {
        content: 'whitespace-nowrap',
      },
      truncate: {
        base: 'min-w-0',
        cluster: 'min-w-0 w-full',
        content: 'truncate',
      },
      'truncate-wrap': {
        content: 'truncate md:whitespace-pre-wrap',
      },
      wrap: {
        content: 'whitespace-pre-wrap',
      },
    },

    size: {
      lg: {
        base: 'px-3 py-1 text-sm',
      },
      md: {
        base: 'px-2.5 py-0.5 text-sm',
      },
      sm: {
        base: 'px-2 py-0.5 text-xs',
      },
      xs: {
        base: 'px-1.5 py-0.5 text-xs',
      },
    },

    variant: {
      brand: {
        base: 'bg-tremor-brand-faint ring-tremor-brand-subtle',
        content: 'text-tremor-brand',
        dot: 'bg-tremor-brand',
      },
      failure: {
        base: 'bg-red-50 ring-red-600/20 dark:bg-red-900/30 dark:ring-red-600/30',
        content: 'text-red-900 dark:text-red-400',
        dot: 'bg-red-500',
      },
      info: {
        base: 'bg-blue-50 ring-blue-600/20 dark:bg-blue-900/30 dark:ring-blue-600/30',
        content: 'text-blue-900 dark:text-blue-400',
        dot: 'bg-blue-500',
      },
      inverse: {
        base: 'bg-slate-600 ring-slate-600/30 dark:bg-tremor-background-emphasis dark:ring-tremor-border',
        content: 'text-tremor-content-inverted',
        dot: 'bg-gray-100 dark:bg-tremor-background-subtle',
      },
      neutral: {
        base: 'bg-gray-100 ring-gray-500/30 dark:bg-tremor-background-subtle dark:ring-tremor-border',
        content: 'text-tremor-content-emphasis',
        dot: 'bg-gray-500 dark:bg-tremor-content',
      },
      outline: {
        base: 'bg-tremor-background ring-tremor-border',
        content: 'text-tremor-content-strong',
        dot: 'bg-blue-500',
      },
      plain: {
        base: 'bg-transparent ring-0',
        content: 'text-tremor-content-strong',
        dot: 'bg-tremor-brand',
      },
      success: {
        base: 'bg-emerald-50 ring-emerald-600/30 dark:bg-emerald-900/30 dark:ring-emerald-600/30',
        content: 'text-emerald-900 dark:text-emerald-400',
        dot: 'bg-emerald-500',
      },
    },

    weight: {
      light: 'font-light',
      medium: 'font-medium',
      normal: 'font-normal',
    },
  },
})

const tagDotVariants = tv({
  base: 'size-2 shrink-0 rounded-full',

  defaultVariants: {
    ring: 'none',
    variant: 'neutral',
  },

  variants: {
    ring: {
      none: null,
      subtle: 'ring-1 ring-gray-100 dark:ring-tremor-background-subtle',
    },

    variant: {
      brand: 'bg-tremor-brand',
      categoryAmber: 'bg-category-amber',
      categoryBlue: 'bg-category-blue',
      categoryEmerald: 'bg-category-emerald',
      categoryGray: 'bg-category-gray',
      categoryRed: 'bg-category-red',
      categorySky: 'bg-category-sky',
      categoryViolet: 'bg-category-violet',
      failure: 'bg-red-500',
      info: 'bg-blue-500',
      inverse: 'bg-gray-100 dark:bg-tremor-background-subtle',
      muted: 'bg-gray-300 dark:bg-gray-600',
      neutral: 'bg-gray-500 dark:bg-tremor-content',
      outline: 'bg-blue-500',
      plain: 'bg-tremor-brand',
      success: 'bg-emerald-500',
      warning: 'bg-orange-500',
    },
  },
})

export interface TagProps
  extends
    Omit<HTMLAttributes<HTMLDivElement>, 'className'>,
    VariantProps<typeof tagVariants> {
  /**
   * @default false
   */
  asChild?: boolean
  children: string | ReactElement<unknown>
  dot?: ReactElement<unknown>
  endAdornment?: ReactNode
}

export interface TagDotProps
  extends
    Omit<ComponentProps<'span'>, 'className'>,
    VariantProps<typeof tagDotVariants> {}

export interface TagButtonProps extends Omit<
  ButtonProps,
  'asChild' | 'children' | 'endIcon' | 'startIcon'
> {
  children: string | ReactElement<unknown>
  dot?: ReactElement<unknown>
  dotVariant?: VariantProps<typeof tagVariants>['variant']
  endAdornment?: ReactNode
  overflowBehavior?: VariantProps<typeof tagVariants>['overflowBehavior']
}

export function TagDot({ ring, variant, ...props }: TagDotProps) {
  return <span className={tagDotVariants({ ring, variant })} {...props} />
}

export function Tag({
  asChild = false,
  children,
  dot,
  endAdornment,
  overflowBehavior,
  size,
  variant,
  weight,
  ...props
}: TagProps) {
  const Component = asChild ? Slot : 'span'

  const classNames = tagVariants({
    overflowBehavior,
    size,
    variant,
    weight,
  })

  return (
    <Component className={classNames.base()} {...props}>
      {asChild ? (
        isValidElement<{ children: ReactNode }>(children) ? (
          // oxlint-disable-next-line react/no-clone-element
          cloneElement(
            children,
            undefined,
            dot,
            <span className={classNames.content()}>
              {children.props.children}
            </span>,
            endAdornment,
          )
        ) : null
      ) : (
        <>
          {dot}
          <span className={classNames.content()}>
            <Slottable>{children}</Slottable>
          </span>
          {endAdornment}
        </>
      )}
    </Component>
  )
}

export function TagButton({
  children,
  dot,
  dotVariant,
  endAdornment,
  overflowBehavior,
  ...props
}: TagButtonProps) {
  const classNames = tagVariants({
    overflowBehavior,
  })

  return (
    <Button align="start" {...props}>
      <span className={classNames.cluster()}>
        {dot ?? <TagDot variant={dotVariant} />}
        <span className={classNames.content()}>
          <Slottable>{children}</Slottable>
        </span>
        {endAdornment}
      </span>
    </Button>
  )
}
